import { isSignal, Signal } from '@angular/core';
import {
  getPropertyPath,
  isDefined,
  isDefinedNotEmpty,
  IsoDate,
  IsoDateTime,
} from '@softline/core';
import { FormatOptions, Formatter } from '@softline/ui-core';
import type { ColGroupDef } from 'ag-grid-community';
import type { CellClassParams, ColDef, ValueSetterParams } from 'ag-grid-enterprise';
import { DataSource, isQueryDataSource } from '@softapps/core';
import {
  AmountColumn,
  BooleanColumn,
  Column,
  DateColumn,
  DateTimeColumn,
  EntityColumn,
  GroupColumn,
  NumberColumn,
  ObjectColumn,
  QuantityColumn,
  StringColumn,
  ValueColumnBase,
} from '../types/grid';
import { ColumnOptions } from '../types/column-options';
import { AmountCellEditor } from './editors/amount-editor.component';
import { BooleanCellEditor } from './editors/boolean-editor.component';
import { DateCellEditor } from './editors/date-editor.component';
import { DateTimeCellEditor } from './editors/date-time-editor.component';
import { NumberCellEditor } from './editors/number-editor.component';
import { QuantityCellEditor } from './editors/quantity-editor.component';
import { TextCellEditor } from './editors/text-editor.component';

function numericCellStyle(
  cellStyle: ColDef['cellStyle'],
  align: 'left' | 'right' = 'right'
): ColDef['cellStyle'] {
  const base = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: align === 'left' ? 'flex-start' : 'flex-end',
  };
  if (cellStyle && typeof cellStyle === 'object')
    return { ...base, ...(cellStyle as Record<string, string>) };
  return cellStyle ?? base;
}

export function toAgGridColDefs<T extends object>(
  columns: Column<T>[],
  formatter: Formatter,
  loadFilterCallback: (col: Column<T>) => Promise<unknown[]>,
  dataSource: DataSource<T>,
  masterDetail = false,
  masterColumn?: string | readonly string[]
): (ColDef | ColGroupDef)[] {
  const traverse = [...columns];
  const returnValue: (ColDef | ColGroupDef)[] = [];
  while (traverse.length > 0) {
    const col = traverse.shift();
    if (!col) continue;
    switch (col.type) {
      case 'group':
        returnValue.push(toGroupColDef(col, formatter, loadFilterCallback, dataSource));
        break;
      case 'string':
        returnValue.push(toStringColDef(col, formatter, dataSource));
        break;
      case 'number':
        returnValue.push(toNumberColDef(col, formatter, dataSource));
        break;
      case 'date':
        returnValue.push(toDateColDef(col, formatter, dataSource));
        break;
      case 'dateTime':
        returnValue.push(toDateTimeColDef(col, formatter, dataSource));
        break;
      case 'object':
        returnValue.push(toObjectColDef(col, formatter, dataSource));
        break;
      case 'amount':
        returnValue.push(toAmountColDef(col, formatter, dataSource));
        break;
      case 'quantity':
        returnValue.push(toQuantityColDef(col, formatter, dataSource));
        break;
      case 'entity':
        returnValue.push(toEntityColDef(col, formatter, loadFilterCallback, dataSource));
        break;
      case 'boolean':
        returnValue.push(toBooleanColDef(col, formatter, dataSource));
        break;
    }
    const leaf = col as Pick<ColumnOptions<T, unknown>, 'colSpan' | 'pinned' | 'lockPosition' | 'flex' | 'minWidth'>;
    const built = returnValue.at(-1);
    if (built && !isColGroupDef(built)) {
      const span = leaf.colSpan;
      if (span) built.colSpan = (params) => (params.data ? span(params.data) : 1);
      // entity and group columns build a group def and are skipped here
      if (leaf.pinned !== undefined) built.pinned = leaf.pinned;
      if (leaf.lockPosition !== undefined) built.lockPosition = leaf.lockPosition;
      if (leaf.flex !== undefined) {
        // a growing column must not keep the fixed-width lock
        built.flex = leaf.flex;
        built.minWidth = leaf.minWidth;
        delete built.width;
        delete built.maxWidth;
        built.suppressSizeToFit = false;
      } else if (leaf.minWidth !== undefined) built.minWidth = leaf.minWidth;
    }
  }
  if (masterDetail) {
    const target = pickMasterColumn(returnValue, masterColumn);
    if (target) {
      target.cellRendererParams = {
        suppressCount: true,
        innerRenderer: target.cellRenderer,
      };
      target.cellRenderer = 'agGroupCellRenderer';
      target.pinned = 'left';
      target.lockPosition = 'left';
    }
  }
  return returnValue;
}

function pickMasterColumn(
  cols: (ColDef | ColGroupDef)[],
  masterColumn: string | readonly string[] | undefined
): ColDef | null {
  const candidates =
    typeof masterColumn === 'string'
      ? [masterColumn]
      : Array.isArray(masterColumn)
        ? masterColumn
        : [];
  for (const candidate of candidates) {
    for (const col of cols) {
      if (isColGroupDef(col)) continue;
      const id = col.colId ?? '';
      if (id === candidate || id.endsWith(`.${candidate}`)) {
        return col;
      }
    }
  }
  const first = cols[0];
  return first && !isColGroupDef(first) ? first : null;
}

function toGroupColDef<T extends object>(
  col: GroupColumn<T>,
  formatter: Formatter,
  loadFilterCallback: (col: Column<T>) => Promise<unknown[]>,
  dataSource: DataSource<T>
): ColGroupDef {
  return {
    headerName: col.header(),
    children: toAgGridColDefs(col.children, formatter, loadFilterCallback, dataSource),
  };
}

function toStringColDef<T extends object>(
  col: StringColumn<T>,
  formatter: Formatter,
  dataSource: DataSource<T>
): ColDef {
  const valueFormatter = getValueFormatter(col, formatter);
  const valueGetter = (p) => (isDefined(p?.data) ? col.getValue(p.data) : undefined);
  const valueSetter = (params: ValueSetterParams<T, string>) =>
    isDefined(col.setValue) && col.setValue(params.data, params.newValue ?? '');
  const field = getQueryField(col, dataSource);
  const editable = isEditable(col);
  const colDef: ColDef = {
    cellDataType: 'text',
    colId: col.colId,
    headerName: col.header(),
    valueGetter: valueGetter,
    valueSetter: valueSetter,
    filter: col.filter === false ? false : 'agTextColumnFilter',
    filterValueGetter: valueGetter,
    filterParams: {
      valueFormatter,
    },
    ...(col.sortable !== undefined ? { sortable: col.sortable } : {}),
    suppressHeaderMenuButton: col.suppressHeaderMenuButton,
    headerClass: col.headerClass,
    cellClass:
      col.align === 'right' ? (col.cellClass ?? '') + ' text-right' : col.cellClass,
    cellStyle: col.multiline
      ? { display: 'block' }
      : col.align === 'right'
        ? numericCellStyle(col.cellStyle, 'right')
        : col.cellRenderer
          ? (col.cellStyle ?? { display: 'block' })
          : col.cellStyle,
    field,
    aggFunc: col.aggFunc,
    allowedAggFuncs: ['count', 'concat', 'first', 'last', 'single'],
    enableRowGroup: col.enableRowGroup ?? true,
    rowGroup: col.rowGroup,
    enablePivot: col.enablePivot,
    pivot: col.pivot,
    hide: col.hide,
    sort: col.sort,
    cellRenderer: col.cellRenderer,
    cellEditor: TextCellEditor,
    cellEditorPopup: true,
    cellEditorPopupPosition: 'over',
    cellEditorParams: {
      col: col,
    },
    editable,
    valueFormatter,
    keyCreator: valueFormatter,
    wrapText: col.multiline,
    autoHeight: col.multiline,
    ...pinningColDef(col),
  };
  return colDef;
}

function pinningColDef(col: {
  pinned?: 'left' | 'right' | boolean;
  lockPosition?: 'left' | 'right' | boolean;
  width?: number;
  resizable?: boolean;
}): Partial<ColDef> {
  const colDef: Partial<ColDef> = {};
  if (col.pinned !== undefined) colDef.pinned = col.pinned;
  if (col.lockPosition !== undefined) colDef.lockPosition = col.lockPosition;
  if (col.resizable !== undefined) colDef.resizable = col.resizable;
  if (col.width !== undefined) {
    colDef.width = col.width;
    colDef.minWidth = col.width;
    colDef.maxWidth = col.width;
    colDef.suppressSizeToFit = true;
  }
  return colDef;
}

function toNumberColDef<T extends object>(
  col: NumberColumn<T>,
  formatter: Formatter,
  dataSource: DataSource<T>
): ColDef {
  const valueFormatter = getValueFormatter(col, formatter);
  const valueGetter = (p) => (isDefined(p?.data) ? col.getValue(p.data) : undefined);
  const valueSetter = (params: ValueSetterParams<T, number>) =>
    isDefined(col.setValue) && col.setValue(params.data, params.newValue ?? 0);
  const editable = isEditable(col);
  const field = getQueryField(col, dataSource);
  const colDef: ColDef = {
    cellDataType: 'number',
    colId: col.colId,
    headerName: col.header(),
    valueGetter: valueGetter,
    valueSetter: valueSetter,
    filter: col.filter === false ? false : 'agNumberColumnFilter',
    filterValueGetter: valueGetter,
    filterParams: {
      valueFormatter,
    },
    suppressHeaderMenuButton: col.suppressHeaderMenuButton,
    headerClass: col.headerClass,
    cellClass:
      (col.cellClass ?? '') + (col.align === 'left' ? ' text-left' : ' text-right'),
    cellStyle: numericCellStyle(col.cellStyle, col.align),
    cellClassRules: {
      'text-danger': (params: CellClassParams<T, number>) =>
        typeof params.value === 'number' && params.value < 0,
    },
    field,
    aggFunc: col.aggFunc,
    enableRowGroup: col.enableRowGroup ?? true,
    rowGroup: col.rowGroup,
    enablePivot: col.enablePivot,
    pivot: col.pivot,
    hide: col.hide,
    sort: col.sort,
    cellRenderer: col.cellRenderer,
    cellEditor: NumberCellEditor,
    cellEditorPopup: true,
    cellEditorPopupPosition: 'over',
    cellEditorParams: { col },
    editable,
    valueFormatter,
    keyCreator: valueFormatter,
  };
  return colDef;
}

function toDateColDef<T extends object>(
  col: DateColumn<T>,
  formatter: Formatter,
  dataSource: DataSource<T>
): ColDef {
  const valueFormatter = getValueFormatter(col, formatter);
  const valueGetter = (p) => (isDefined(p?.data) ? col.getValue(p.data) : undefined);
  const valueSetter = (params: ValueSetterParams<T, IsoDate>) =>
    isDefined(col.setValue) &&
    col.setValue(params.data, params.newValue ?? ('1970-01-01' as IsoDate));
  const editable = isEditable(col);
  const field = getQueryField(col, dataSource);
  const colDef = {
    cellDataType: 'dateString',
    colId: col.colId,
    headerName: col.header(),
    valueGetter: valueGetter,
    valueSetter: valueSetter,
    filter: 'agDateColumnFilter',
    filterValueGetter: valueGetter,
    filterParams: {
      valueFormatter,
    },
    headerClass: col.headerClass,
    cellClass: col.cellClass,
    cellStyle: col.cellStyle,
    field,
    enableRowGroup: col.enableRowGroup ?? true,
    rowGroup: col.rowGroup,
    enablePivot: col.enablePivot,
    pivot: col.pivot,
    hide: col.hide,
    sort: col.sort,
    cellRenderer: col.cellRenderer,
    cellEditor: DateCellEditor,
    cellEditorPopup: true,
    cellEditorPopupPosition: 'over' as const,
    cellEditorParams: { col },
    editable,
    valueFormatter,
    keyCreator: valueFormatter,
  };
  return colDef;
}

function toDateTimeColDef<T extends object>(
  col: DateTimeColumn<T, any>,
  formatter: Formatter,
  dataSource: DataSource<T>
): ColDef {
  const valueFormatter = getValueFormatter(col, formatter);
  const valueGetter = (p) => (isDefined(p?.data) ? col.getValue(p.data) : undefined);
  const valueSetter = (params: ValueSetterParams<T, IsoDateTime>) =>
    isDefined(col.setValue) && col.setValue(params.data, params.newValue);
  const editable = isEditable(col);
  const field = getQueryField(col, dataSource);
  const colDef = {
    cellDataType: 'dateTimeString',
    colId: col.colId,
    headerName: col.header(),
    valueGetter: valueGetter,
    valueSetter: valueSetter,
    filter: 'agDateColumnFilter',
    filterValueGetter: valueGetter,
    filterParams: {
      valueFormatter,
    },
    headerClass: col.headerClass,
    cellClass: col.cellClass,
    cellStyle: col.cellStyle,
    field,
    enableRowGroup: col.enableRowGroup ?? true,
    rowGroup: col.rowGroup,
    enablePivot: col.enablePivot,
    pivot: col.pivot,
    hide: col.hide,
    sort: col.sort,
    cellRenderer: col.cellRenderer,
    cellEditor: DateTimeCellEditor,
    cellEditorPopup: true,
    cellEditorPopupPosition: 'over' as const,
    cellEditorParams: { col },
    editable,
    valueFormatter,
    keyCreator: valueFormatter,
  };
  return colDef;
}

function toAmountColDef<T extends object>(
  col: AmountColumn<T>,
  formatter: Formatter,
  dataSource: DataSource<T>
): ColDef {
  const valueFormatter = getValueFormatter(col, formatter);
  const amountFormatter = (v: any) => {
    if (!isDefined(v) || !valueFormatter) return '';
    const currency = col.getCurrency(v.data);
    if (!currency) return '';
    //TODO: Mutliple currencies for Grouping
    return valueFormatter(v, { currency });
  };
  const valueGetter = (p) => (isDefined(p?.data) ? col.getValue(p.data) : undefined);
  const valueSetter = (params: ValueSetterParams<T, number>) =>
    isDefined(col.setValue) && col.setValue(params.data, params.newValue ?? 0);
  const editable = isEditable(col);
  const field = getQueryField(col, dataSource);
  const colDef = {
    cellDataType: 'amount',
    colId: col.colId,
    headerName: col.header(),
    valueGetter: valueGetter,
    valueSetter: valueSetter,
    filter: col.filter === false ? false : 'agNumberColumnFilter',
    filterValueGetter: valueGetter,
    filterParams: {
      valueFormatter,
    },
    suppressHeaderMenuButton: col.suppressHeaderMenuButton,
    cellClass:
      (col.cellClass ?? '') + (col.align === 'left' ? ' text-left' : ' text-right'),
    cellStyle: numericCellStyle(col.cellStyle, col.align),
    cellClassRules: {
      'text-danger': (params: CellClassParams<T, number>) =>
        typeof params.value === 'number' && params.value < 0,
    },
    field,
    aggFunc: col.aggFunc,
    enableRowGroup: col.enableRowGroup ?? true,
    rowGroup: col.rowGroup,
    enablePivot: col.enablePivot,
    pivot: col.pivot,
    hide: col.hide,
    sort: col.sort,
    cellRenderer: col.cellRenderer,
    cellEditor: AmountCellEditor,
    cellEditorPopup: true,
    cellEditorPopupPosition: 'over' as const,
    cellEditorParams: { col },
    editable,
    valueFormatter: amountFormatter,
    keyCreator: valueFormatter,
  };
  return colDef;
}

function toQuantityColDef<T extends object>(
  col: QuantityColumn<T>,
  formatter: Formatter,
  dataSource: DataSource<T>
): ColDef {
  const valueFormatter = getValueFormatter(col, formatter);
  const amountFormatter = (v: any) => {
    if (!isDefined(v) || !valueFormatter) return '';
    const value = valueFormatter(v);
    if (!isDefinedNotEmpty(value)) return '';
    const unit = col.getUnit?.(v.data);
    return unit ? `${value} ${unit}` : value;
  };
  const valueGetter = (p) => (isDefined(p?.data) ? col.getValue(p.data) : undefined);
  const valueSetter = (params: ValueSetterParams<T, number>) =>
    isDefined(col.setValue) && col.setValue(params.data, params.newValue ?? 0);
  const editable = isEditable(col);
  const field = getQueryField(col, dataSource);
  const colDef = {
    cellDataType: 'quantity',
    colId: col.colId,
    headerName: col.header(),
    valueGetter: valueGetter,
    valueSetter: valueSetter,
    filter: col.filter === false ? false : 'agNumberColumnFilter',
    filterValueGetter: valueGetter,
    filterParams: {
      valueFormatter,
    },
    suppressHeaderMenuButton: col.suppressHeaderMenuButton,
    cellClass:
      (col.cellClass ?? '') + (col.align === 'left' ? ' text-left' : ' text-right'),
    cellStyle: numericCellStyle(col.cellStyle, col.align),
    cellClassRules: {
      'text-danger': (params: CellClassParams<T, number>) =>
        typeof params.value === 'number' && params.value < 0,
    },
    field,
    aggFunc: col.aggFunc,
    enableRowGroup: col.enableRowGroup ?? true,
    rowGroup: col.rowGroup,
    enablePivot: col.enablePivot,
    pivot: col.pivot,
    hide: col.hide,
    sort: col.sort,
    cellRenderer: col.cellRenderer,
    cellEditor: QuantityCellEditor,
    cellEditorPopup: true,
    cellEditorPopupPosition: 'over' as const,
    cellEditorParams: { col },
    editable,
    valueFormatter: amountFormatter,
    keyCreator: valueFormatter,
  };
  return colDef;
}

function toObjectColDef<T extends object>(
  col: ObjectColumn<T, any>,
  formatter: Formatter,
  dataSource: DataSource<T>
): ColDef {
  const valueFormatter = getValueFormatter(col, formatter);
  const valueGetter = (p) => (isDefined(p?.data) ? col.getValue(p.data) : undefined);
  const valueSetter = (params: ValueSetterParams<T, object>) =>
    isDefined(col.setValue) && col.setValue(params.data, params.newValue);
  const editable = isEditable(col);
  const field = getQueryField(col, dataSource);
  const colDef = {
    cellDataType: 'object',
    colId: col.colId,
    headerName: col.header(),
    valueGetter: valueGetter,
    valueSetter: valueSetter,
    filter: 'agSetColumnFilter',
    filterValueGetter: valueGetter,
    filterParams: {
      valueFormatter,
    },
    headerClass: col.headerClass,
    cellClass: col.cellClass,
    cellStyle: col.cellStyle,
    field,
    enableRowGroup: col.enableRowGroup ?? true,
    rowGroup: col.rowGroup,
    enablePivot: col.enablePivot,
    pivot: col.pivot,
    hide: col.hide,
    editable,
    valueFormatter,
    keyCreator: valueFormatter,
    comparator: col.sortBy
      ? (a, b, n1, n2, isDescending) => {
          if (!col.sortBy) return 0;
          const aVal = col.sortBy(a);
          const bVal = col.sortBy(b);
          return aVal === bVal ? 0 : aVal < bVal ? -1 : 1;
        }
      : undefined,
  };
  return colDef;
}

function toEntityColDef<T extends object>(
  col: EntityColumn<T, any>,
  formatter: Formatter,
  loadFilterCallback: (col: Column<any>) => Promise<unknown[]>,
  dataSource: DataSource<T>
): ColGroupDef {
  const valueFormatter = getValueFormatter(col, formatter);
  const valueGetter = (p) => (isDefined(p?.data) ? col.getValue(p.data) : undefined);
  const valueSetter = (params: ValueSetterParams<T, object>) =>
    isDefined(col.setValue) && col.setValue(params.data, params.newValue);
  const editable = isEditable(col);
  const filterParams: any = isQueryDataSource(dataSource)
    ? {
        values: async (params) => {
          const result = await loadFilterCallback(col);
          params.success(result);
        },
        keyCreator: (o) => o.value.id,
        valueFormatter: valueFormatter,
        refreshValuesOnOpen: true,
      }
    : { valueFormatter };

  const field = getQueryField(col, dataSource);

  const colDef: ColGroupDef = {
    headerName: col.header(),
    marryChildren: true,
    headerClass: col.headerClass ?? undefined,
    children: [
      {
        cellDataType: 'entity',
        colId: col.colId,
        headerName: col.header(),
        valueGetter,
        valueSetter,
        filter: 'agSetColumnFilter',
        filterValueGetter: valueGetter,
        filterParams: filterParams,
        enableRowGroup: col.enableRowGroup ?? true,
        rowGroup: col.rowGroup,
        enablePivot: col.enablePivot,
        pivot: col.pivot,
        hide: col.hide,
        cellRenderer: col.cellRenderer,
        minWidth: 400,
        editable,
        valueFormatter,
        keyCreator: valueFormatter,
        columnGroupShow: 'closed',
        comparator: col.sortBy
          ? (a, b, n1, n2, isDescending) => {
              if (!col.sortBy) return 0;
              const aVal = col.sortBy(a);
              const bVal = col.sortBy(b);
              return aVal === bVal ? 0 : aVal < bVal ? -1 : 1;
            }
          : undefined,
      },
      ...toAgGridColDefs<T>(col.children, formatter, loadFilterCallback, dataSource).map(
        (o) => {
          if (isColGroupDef(o)) return o;
          const childField = isQueryDataSource(dataSource)
            ? `${field}.${o.field}`
            : undefined;
          return {
            ...o,
            colId: `${col.colId}.${o.colId}`,
            valueGetter: (p) =>
              o.valueGetter && typeof o.valueGetter !== 'string'
                ? o.valueGetter({ ...p, data: valueGetter({ data: p.data }) })
                : undefined,
            field: childField,
            hide: col.hide,
            columnGroupShow: 'open' as const,
          };
        }
      ),
    ],
  };
  return colDef;
}

function toBooleanColDef<T extends object>(
  col: BooleanColumn<T>,
  formatter: Formatter,
  dataSource: DataSource<T>
): ColDef {
  const valueGetter = (p) => (isDefined(p?.data) ? col.getValue(p.data) : undefined);
  const valueSetter = (params: ValueSetterParams<T, boolean>) =>
    isDefined(col.setValue) && col.setValue(params.data, params.newValue as any);
  const editable = isEditable(col);
  const field = getQueryField(col, dataSource);
  return {
    cellDataType: 'boolean',
    colId: col.colId,
    headerName: col.header(),
    valueGetter,
    valueSetter,
    filter: 'agSetColumnFilter',
    filterValueGetter: valueGetter,
    headerClass: col.headerClass,
    cellClass: col.cellClass,
    cellStyle: col.cellStyle,
    field,
    aggFunc: col.aggFunc,
    enableRowGroup: col.enableRowGroup ?? true,
    rowGroup: col.rowGroup,
    enablePivot: col.enablePivot,
    pivot: col.pivot,
    hide: col.hide,
    sort: col.sort,
    cellRenderer: col.cellRenderer,
    cellEditor: BooleanCellEditor,
    cellEditorPopup: false,
    cellEditorParams: { col },
    editable,
  };
}

function getValueFormatter(
  col: {
    format?: string | ((v: any) => string | Signal<string>);
    preventNumberDefault?: boolean;
  },
  formatter: Formatter
): ((v: any, formatOptions?: FormatOptions) => string) | undefined {
  if (!col.format) return undefined;
  return (v: any, formatOptions?: FormatOptions) => {
    if (!isDefined(v?.value)) return undefined;
    if (typeof v.value === 'number' && Number.isNaN(v.value)) return '';
    if (!col.format) return v.value;
    const formatted =
      typeof col.format === 'string'
        ? formatter(v.value, col.format, {
            ...formatOptions,
            preventNumberDefault: col.preventNumberDefault,
          })
        : col.format(v.value);
    if (isSignal(formatted)) return formatted();
    return formatted;
  };
}

function getQueryField<T extends object>(
  col: { getValue: (row: T) => unknown; queryFunction?: string },
  dataSource: DataSource<T>
): string | undefined {
  if (!isQueryDataSource(dataSource)) return undefined;
  const path = getPropertyPath(col.getValue);
  if (!path) return undefined;
  return col.queryFunction ? `${path}:${col.queryFunction}` : path;
}

function isColGroupDef(col: ColDef | ColGroupDef): col is ColGroupDef {
  return (col as ColGroupDef).children !== undefined;
}

function isEditable<TRow extends object, TValue>(col: ValueColumnBase<TRow, TValue>) {
  if (!col.setValue) return false;

  return (params: { data: TRow }) =>
    typeof col.readonly === 'undefined'
      ? true
      : typeof col.readonly === 'boolean'
        ? !col.readonly
        : !col.readonly(params.data);
}

/** Columns `fitCellContents` may size. AG drops `flex` from every column it auto-sizes, so a grid
 *  with growing columns sizes only the others; undefined keeps the default (all columns). */
export function autoSizeColIds(
  colDefs: readonly (ColDef | ColGroupDef)[],
  all = false
): string[] | undefined {
  const leaves: ColDef[] = [];
  const walk = (defs: readonly (ColDef | ColGroupDef)[]) =>
    defs.forEach((def) => (isColGroupDef(def) ? walk(def.children) : leaves.push(def)));
  walk(colDefs);
  if (!all && !leaves.some((def) => def.flex)) return undefined;
  return leaves.filter((def) => !def.flex && def.colId).map((def) => def.colId as string);
}

